import { StandardEvents } from '@shopify/events';

class FurseraQuantityBundle extends HTMLElement {
  #connected = false;
  #variants = [];
  #eligibleVariants = [];
  #optionNames = [];
  #quantities = [];
  #productScope = null;
  #selections = new Map();
  #presentmentCurrencyRate = 1;
  #checkoutButtonSyncFrame = 0;
  #checkoutPending = false;

  connectedCallback() {
    if (this.#connected) return;
    this.#connected = true;

    this.#variants = this.#parseJson('[data-bundle-variants]', []);
    this.#optionNames = this.#parseJson('[data-bundle-option-names]', []);
    this.#quantities = Array.from(this.querySelectorAll('[data-bundle-option]'), (option) =>
      Number(option.dataset.bundleOption),
    ).filter((quantity) => Number.isInteger(quantity) && quantity > 0);

    if (this.#quantities.length === 0) {
      this.hidden = true;
      return;
    }

    this.#presentmentCurrencyRate = this.#resolvePresentmentCurrencyRate();
    if (!this.#presentmentCurrencyRate) {
      this.hidden = true;
      return;
    }

    const basePriceLimit = Number(this.dataset.basePriceLimit || 3900);
    const presentmentMaxVariantPrice = Math.max(
      0,
      ...this.#variants.map((variant) => Number(variant.price) || 0),
    );
    const shopCurrencyMaxVariantPrice =
      presentmentMaxVariantPrice / this.#presentmentCurrencyRate;
    this.#eligibleVariants =
      shopCurrencyMaxVariantPrice < basePriceLimit
        ? this.#variants.filter((variant) => variant.available)
        : [];

    if (this.#eligibleVariants.length === 0) {
      this.hidden = true;
      return;
    }

    const optionCount = Math.max(
      1,
      ...this.#variants.map((variant) => this.#variantOptions(variant).length),
    );
    while (this.#optionNames.length < optionCount) {
      this.#optionNames.push(`Option ${this.#optionNames.length + 1}`);
    }

    this.addEventListener('change', this.#handleChange);
    this.addEventListener('input', this.#handleInput);
    this.#productScope = this.closest('product-component');
    this.#productScope?.addEventListener(StandardEvents.productSelect, this.#handleProductSelect);
    this.#syncPriceColor();

    this.#setCurrentVariant(this.dataset.currentVariantId, false);
    this.dataset.packQuantity = String(this.#selectedQuantity());
    this.#syncNativeQuantity();
  }

  disconnectedCallback() {
    this.removeEventListener('change', this.#handleChange);
    this.removeEventListener('input', this.#handleInput);
    this.#productScope?.removeEventListener(StandardEvents.productSelect, this.#handleProductSelect);
    if (this.#checkoutButtonSyncFrame) {
      window.cancelAnimationFrame(this.#checkoutButtonSyncFrame);
      this.#checkoutButtonSyncFrame = 0;
    }

    // Shopify can temporarily move product blocks while hydrating the page.
    // Allow the element to initialize again so selector and variant listeners
    // are restored when it is reconnected.
    this.#productScope = null;
    this.#connected = false;
  }

  #parseJson(selector, fallback) {
    try {
      return JSON.parse(this.querySelector(selector)?.textContent || JSON.stringify(fallback));
    } catch {
      return fallback;
    }
  }

  #resolvePresentmentCurrencyRate() {
    const activeCurrency = String(this.dataset.currency || '').toUpperCase();
    const shopCurrency = String(this.dataset.shopCurrency || 'USD').toUpperCase();
    if (!activeCurrency || activeCurrency === shopCurrency) return 1;

    const rate = Number(
      window.Shopify?.currency?.rate || window.ShopifyAnalytics?.meta?.currencyRate,
    );
    return Number.isFinite(rate) && rate > 0 ? rate : 0;
  }

  #handleChange = (event) => {
    if (event.target instanceof HTMLInputElement && event.target.type === 'radio') {
      this.#setPackQuantity(Number(event.target.value));
      return;
    }

    if (
      event.target instanceof HTMLSelectElement &&
      event.target.matches('[data-bundle-option-select]')
    ) {
      this.#handleSelectorChange(event.target);
    }
  };

  #handleInput = (event) => {
    if (
      event.target instanceof HTMLSelectElement &&
      event.target.matches('[data-bundle-option-select]')
    ) {
      this.#handleSelectorChange(event.target);
    }
  };

  #handleProductSelect = (event) => {
    event.promise
      ?.then(({ detail }) => {
        const resource = detail?.resource;
        if (!resource?.id) return;
        this.#setCurrentVariant(String(resource.id));
      })
      .catch((error) => {
        if (error?.name !== 'AbortError') {
          console.warn('[quantity-bundle] Variant update failed:', error);
        }
      });
  };

  #selectedQuantity() {
    return Number(
      this.querySelector('input[type="radio"]:checked')?.value ||
        this.dataset.defaultPackQuantity ||
        this.#quantities[0] ||
        1,
    );
  }

  #variantOptions(variant) {
    if (Array.isArray(variant?.options)) {
      return variant.options.map((value) => String(value));
    }

    return [variant?.option1, variant?.option2, variant?.option3]
      .filter((value) => value != null)
      .map((value) => String(value));
  }

  #variantById(variantId) {
    return this.#variants.find((variant) => String(variant.id) === String(variantId)) || null;
  }

  #eligibleVariantById(variantId) {
    return (
      this.#eligibleVariants.find((variant) => String(variant.id) === String(variantId)) || null
    );
  }

  #setCurrentVariant(variantId, preserveSelections = true) {
    const variant = this.#eligibleVariantById(variantId);

    if (!variant) {
      this.hidden = true;
      this.removeAttribute('data-ready');
      return;
    }

    const previousVariantId = String(this.dataset.currentVariantId || '');
    const nextVariantId = String(variant.id);
    this.dataset.currentVariantId = nextVariantId;
    this.dataset.currentUnitPrice = String(variant.price);
    this.hidden = false;
    this.dataset.ready = '';

    for (const quantity of this.#quantities) {
      const previousSelection = this.#selections.get(quantity);
      const nextSelection = Array.from({ length: quantity }, (_, index) => {
        const savedId = preserveSelections ? previousSelection?.[index] : null;
        if (!savedId || savedId === previousVariantId || !this.#eligibleVariantById(savedId)) {
          return nextVariantId;
        }
        return String(savedId);
      });
      this.#selections.set(quantity, nextSelection);
    }

    this.#renderSelectedSelectors();
    this.#updateSelectedState();
    this.#updatePrices();
    this.#syncPriceColor();
    this.#scheduleBundleCheckoutButtonSync();
  }

  #syncPriceColor() {
    const productPrice = this.#productScope?.querySelector('product-price .price');
    if (!(productPrice instanceof HTMLElement)) return;
    this.style.setProperty('--bundle-price-color', getComputedStyle(productPrice).color);
  }

  #setPackQuantity(quantity) {
    const defaultQuantity = Number(this.dataset.defaultPackQuantity || this.#quantities[0] || 1);
    const normalizedQuantity = this.#quantities.includes(quantity) ? quantity : defaultQuantity;
    this.dataset.packQuantity = String(normalizedQuantity);
    this.#syncNativeQuantity();
    this.#renderSelectedSelectors();
    this.#updateSelectedState();
    this.#updatePrices();
    this.#scheduleBundleCheckoutButtonSync();
  }

  #scheduleBundleCheckoutButtonSync() {
    if (this.#checkoutButtonSyncFrame) {
      window.cancelAnimationFrame(this.#checkoutButtonSyncFrame);
    }

    this.#checkoutButtonSyncFrame = window.requestAnimationFrame(() => {
      this.#checkoutButtonSyncFrame = 0;
      this.#syncBundleCheckoutButton();
    });
  }

  #syncBundleCheckoutButton() {
    const acceleratedCheckout = this.#productScope?.querySelector('.accelerated-checkout-block');
    if (!(acceleratedCheckout instanceof HTMLElement)) return;

    let button = acceleratedCheckout.querySelector('[data-fursera-bundle-buy-now]');
    if (!(button instanceof HTMLButtonElement)) {
      button = document.createElement('button');
      button.type = 'button';
      button.className = 'fursera-bundle-buy-now';
      button.dataset.furseraBundleBuyNow = '';
      button.textContent = 'Buy now';
      button.addEventListener('click', this.#handleBundleCheckout);
      acceleratedCheckout.append(button);
    }

    const bundleActive = this.#selectedQuantity() > 1;
    acceleratedCheckout.dataset.furseraBundleActive = String(bundleActive);
    button.hidden = !bundleActive;
  }

  #handleBundleCheckout = async (event) => {
    event.preventDefault();
    if (this.#checkoutPending) return;

    const button = event.currentTarget;
    const items = this.getCartItems();
    const totalQuantity = items.reduce((total, item) => total + item.quantity, 0);
    if (!(button instanceof HTMLButtonElement) || totalQuantity <= 1) return;

    this.#checkoutPending = true;
    button.disabled = true;
    button.setAttribute('aria-busy', 'true');
    button.textContent = 'Processing…';

    const shopRoot = window.Shopify?.routes?.root || '/';

    try {
      const response = await fetch(`${shopRoot}cart/add.js`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
        },
        body: JSON.stringify({
          items: items.map((item) => ({
            id: Number(item.variantId),
            quantity: item.quantity,
          })),
        }),
      });
      const result = await response.json();
      if (!response.ok || result?.status) {
        throw new Error(result?.description || result?.message || 'Unable to start checkout');
      }

      window.location.assign(`${shopRoot}checkout`);
    } catch (error) {
      console.error('[quantity-bundle] Direct checkout failed:', error);
      button.disabled = false;
      button.removeAttribute('aria-busy');
      button.textContent = 'Try again';
      this.#checkoutPending = false;

      const liveRegion = this.#productScope?.querySelector('[ref="liveRegion"]');
      if (liveRegion instanceof HTMLElement) {
        liveRegion.textContent = error instanceof Error ? error.message : 'Unable to start checkout';
      }
    }
  };

  #syncNativeQuantity() {
    const input = this.#productScope?.querySelector(
      'product-form-component form[data-type="add-to-cart-form"] input[name="quantity"]',
    );
    if (!(input instanceof HTMLInputElement) || input.value === '1') return;

    input.value = '1';
    input.dispatchEvent(new Event('input', { bubbles: true }));
    input.dispatchEvent(new Event('change', { bubbles: true }));
  }

  #updateSelectedState() {
    const selectedQuantity = this.#selectedQuantity();

    for (const option of this.querySelectorAll('[data-bundle-option]')) {
      const quantity = Number(option.dataset.bundleOption);
      const isSelected = quantity === selectedQuantity;
      option.dataset.selected = String(isSelected);

      const radio = option.querySelector('input[type="radio"]');
      const selectors = option.querySelector('[data-bundle-selectors]');
      if (radio instanceof HTMLInputElement) {
        radio.setAttribute('aria-expanded', String(isSelected && quantity > 1));
      }
      if (selectors instanceof HTMLElement) {
        selectors.hidden = !isSelected || this.#eligibleVariants.length <= 1;
      }
    }
  }

  #renderSelectedSelectors() {
    const selectedQuantity = this.#selectedQuantity();

    for (const quantity of this.#quantities.filter((tierQuantity) => tierQuantity > 1)) {
      const list = this.querySelector(`[data-bundle-selector-list="${quantity}"]`);
      if (quantity === selectedQuantity) {
        this.#renderTierSelectors(quantity);
      } else if (list instanceof HTMLElement) {
        list.replaceChildren();
      }
    }
  }

  #renderTierSelectors(quantity) {
    const list = this.querySelector(`[data-bundle-selector-list="${quantity}"]`);
    if (!(list instanceof HTMLElement)) return;

    if (this.#eligibleVariants.length <= 1) {
      list.replaceChildren();
      return;
    }

    const selection = this.#selections.get(quantity) || [];
    const fragment = document.createDocumentFragment();

    for (let itemIndex = 0; itemIndex < quantity; itemIndex += 1) {
      const variant =
        this.#eligibleVariantById(selection[itemIndex]) ||
        this.#eligibleVariantById(this.dataset.currentVariantId) ||
        this.#eligibleVariants[0];
      const selectedOptions = this.#variantOptions(variant);

      const item = document.createElement('div');
      item.className = 'fursera-quantity-bundle__selector-item';
      item.dataset.bundleItemIndex = String(itemIndex);
      item.dataset.selectedVariantId = String(variant.id);

      const number = document.createElement('span');
      number.className = 'fursera-quantity-bundle__selector-number';
      number.textContent = `#${itemIndex + 1}`;

      const fields = document.createElement('div');
      fields.className = 'fursera-quantity-bundle__selector-fields';
      fields.style.setProperty('--bundle-option-count', String(this.#optionNames.length));

      for (let optionIndex = 0; optionIndex < this.#optionNames.length; optionIndex += 1) {
        const field = document.createElement('label');
        field.className = 'fursera-quantity-bundle__selector-field';

        const label = document.createElement('span');
        label.className = 'fursera-quantity-bundle__selector-label';
        label.textContent = this.#optionNames[optionIndex];

        const select = document.createElement('select');
        select.className = 'fursera-quantity-bundle__selector';
        select.dataset.bundleOptionSelect = '';
        select.dataset.bundleQuantity = String(quantity);
        select.dataset.bundleItemIndex = String(itemIndex);
        select.dataset.bundleOptionIndex = String(optionIndex);
        select.setAttribute(
          'aria-label',
          `${this.#optionNames[optionIndex]} for item ${itemIndex + 1}`,
        );

        for (const value of this.#optionValues(optionIndex)) {
          const option = document.createElement('option');
          option.value = value;
          option.textContent = value;
          option.selected = value === selectedOptions[optionIndex];
          select.append(option);
        }

        field.append(label, select);
        fields.append(field);
      }

      item.append(number, fields);
      fragment.append(item);
    }

    list.replaceChildren(fragment);

    for (const item of list.querySelectorAll('[data-bundle-item-index]')) {
      this.#refreshItemControls(item);
    }
  }

  #optionValues(optionIndex) {
    const values = [];
    const seen = new Set();

    for (const variant of this.#variants) {
      const value = this.#variantOptions(variant)[optionIndex];
      if (value == null || seen.has(value)) continue;
      seen.add(value);
      values.push(value);
    }

    return values;
  }

  #handleSelectorChange(select) {
    const quantity = Number(select.dataset.bundleQuantity);
    const itemIndex = Number(select.dataset.bundleItemIndex);
    const optionIndex = Number(select.dataset.bundleOptionIndex);
    const item = select.closest('[data-bundle-item-index]');
    if (
      !(item instanceof HTMLElement) ||
      quantity <= 1 ||
      !this.#quantities.includes(quantity)
    ) return;

    const requestedOptions = Array.from(
      item.querySelectorAll('[data-bundle-option-select]'),
      (control) => control.value,
    );
    const variant = this.#findBestVariant(requestedOptions, optionIndex);
    if (!variant) return;

    const nextSelection = [...(this.#selections.get(quantity) || [])];
    nextSelection[itemIndex] = String(variant.id);
    this.#selections.set(quantity, nextSelection);
    item.dataset.selectedVariantId = String(variant.id);

    this.#setItemValues(item, variant);
    this.#refreshItemControls(item);
    this.#updatePrices();
  }

  #findBestVariant(requestedOptions, changedOptionIndex) {
    const exact = this.#eligibleVariants.find((variant) =>
      this.#variantOptions(variant).every(
        (value, index) => value === String(requestedOptions[index]),
      ),
    );
    if (exact) return exact;

    const requestedValue = String(requestedOptions[changedOptionIndex]);
    const candidates = this.#eligibleVariants.filter(
      (variant) => this.#variantOptions(variant)[changedOptionIndex] === requestedValue,
    );
    if (candidates.length === 0) return null;

    return candidates
      .map((variant) => {
        const options = this.#variantOptions(variant);
        const score = options.reduce((total, value, index) => {
          if (index === changedOptionIndex) return total;
          const weight = index < changedOptionIndex ? 4 : 1;
          return total + (value === String(requestedOptions[index]) ? weight : 0);
        }, 0);
        return { variant, score };
      })
      .sort((left, right) => right.score - left.score)[0].variant;
  }

  #setItemValues(item, variant) {
    const variantOptions = this.#variantOptions(variant);
    for (const select of item.querySelectorAll('[data-bundle-option-select]')) {
      const optionIndex = Number(select.dataset.bundleOptionIndex);
      select.value = variantOptions[optionIndex];
    }
  }

  #refreshItemControls(item) {
    const variant = this.#eligibleVariantById(item.dataset.selectedVariantId);
    if (!variant) return;

    const selectedOptions = this.#variantOptions(variant);
    for (const select of item.querySelectorAll('[data-bundle-option-select]')) {
      const optionIndex = Number(select.dataset.bundleOptionIndex);

      for (const option of select.options) {
        const status = this.#optionStatus(optionIndex, option.value, selectedOptions);
        option.disabled = status !== 'available';
        option.textContent =
          status === 'available'
            ? option.value
            : `${option.value} — ${status === 'sold-out' ? 'Sold out' : 'Unavailable'}`;
      }

      select.value = selectedOptions[optionIndex];
    }
  }

  #optionStatus(optionIndex, value, selectedOptions) {
    const matchesPriorOptions = (variant) => {
      const options = this.#variantOptions(variant);
      if (options[optionIndex] !== value) return false;
      return options
        .slice(0, optionIndex)
        .every((priorValue, index) => priorValue === selectedOptions[index]);
    };

    if (this.#eligibleVariants.some(matchesPriorOptions)) return 'available';

    const matchingVariants = this.#variants.filter(matchesPriorOptions);
    if (matchingVariants.length > 0 && matchingVariants.every((variant) => !variant.available)) {
      return 'sold-out';
    }

    return 'unavailable';
  }

  #selectedVariantIds(quantity = this.#selectedQuantity()) {
    if (quantity === 1) return [String(this.dataset.currentVariantId)];

    const selection = [...(this.#selections.get(quantity) || [])];
    while (selection.length < quantity) {
      selection.push(String(this.dataset.currentVariantId));
    }

    return selection.slice(0, quantity);
  }

  #fullPriceForQuantity(quantity) {
    return this.#selectedVariantIds(quantity).reduce((total, variantId) => {
      const variant = this.#variantById(variantId);
      return total + Number(variant?.price || 0);
    }, 0);
  }

  #discountedPriceForQuantity(quantity, discount) {
    return this.#selectedVariantIds(quantity).reduce((total, variantId) => {
      const unitPrice = Number(this.#variantById(variantId)?.price || 0);
      const unitDiscount = Math.floor(unitPrice * (discount / 100));
      return total + unitPrice - unitDiscount;
    }, 0);
  }

  #updatePrices() {
    let selectedDiscountedPrice = 0;
    const selectedQuantity = this.#selectedQuantity();

    for (const quantity of this.#quantities) {
      const fullPrice = this.#fullPriceForQuantity(quantity);
      const tier = this.querySelector(`[data-bundle-option="${quantity}"]`);
      const discount = Number(tier?.dataset.bundleDiscount || 0);
      const discountedPrice = this.#discountedPriceForQuantity(quantity, discount);
      const price = this.querySelector(`[data-bundle-price="${quantity}"]`);
      const compare = this.querySelector(`[data-bundle-compare="${quantity}"]`);
      const freeShipping = this.querySelector(`[data-bundle-free-shipping="${quantity}"]`);
      const baseFreeShippingThreshold = Number(
        this.dataset.baseFreeShippingThreshold || 3900,
      );
      const freeShippingThreshold = Math.round(
        baseFreeShippingThreshold * this.#presentmentCurrencyRate,
      );

      if (price) price.textContent = this.#formatMoney(discountedPrice);
      if (compare) compare.textContent = this.#formatMoney(fullPrice);
      if (freeShipping instanceof HTMLElement) {
        freeShipping.hidden = discountedPrice < freeShippingThreshold;
      }

      if (quantity === selectedQuantity) selectedDiscountedPrice = discountedPrice;
    }

    this.dispatchEvent(
      new CustomEvent('fursera:bundle:change', {
        bubbles: true,
        detail: {
          amount: (selectedDiscountedPrice / 100).toFixed(2),
          amountCents: selectedDiscountedPrice,
          shopCurrencyAmountCents: Math.round(
            selectedDiscountedPrice / this.#presentmentCurrencyRate,
          ),
          currencyCode: this.dataset.currency || 'USD',
          quantity: selectedQuantity,
        },
      }),
    );
  }

  #formatMoney(cents) {
    return new Intl.NumberFormat(document.documentElement.lang || 'en-US', {
      style: 'currency',
      currency: this.dataset.currency || 'USD',
    }).format(cents / 100);
  }

  getCartItems() {
    const quantity = this.#selectedQuantity();
    const counts = new Map();

    for (const variantId of this.#selectedVariantIds(quantity)) {
      counts.set(variantId, (counts.get(variantId) || 0) + 1);
    }

    return Array.from(counts, ([variantId, itemQuantity]) => ({
      variantId,
      quantity: itemQuantity,
    }));
  }
}

if (!customElements.get('fursera-quantity-bundle')) {
  customElements.define('fursera-quantity-bundle', FurseraQuantityBundle);
}
